import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const TutorialsPage = () => {
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);

  // Sample tutorial data - in a real app, this would come from an API
  const sampleTutorials = useMemo(() => [
    {
      id: 1,
      title: "Getting Started with TrendVector Platform",
      description: "Learn how to navigate the TrendVector platform and access all its features effectively.",
      content: "This comprehensive tutorial covers everything you need to know to get started with TrendVector...",
      level: "beginner",
      category: "platform",
      duration: "1:02 min",
      videoUrl: "/tutorials/getting-started.mp4",
      youtubeUrl: "https://youtu.be/fS6HSZyZEGo",
      thumbnail: "/assets/tutorials/getting-started.jpg",
      instructor: "TrendVector Team",
      rating: 4.8,
      students: 1250
    },
    {
      id: 2,
      title: "How to Use Long Term Model",
      description: "Learn how to effectively use the Long Term Model for your investment strategy and portfolio management.",
      content: "This tutorial covers how to use the Long Term Model feature, including setting up your portfolio, analyzing long-term trends, and making informed investment decisions...",
      level: "beginner",
      category: "fundamentals",
      duration: "1:24 min",
      videoUrl: "/tutorials/long-term-model.mp4",
      youtubeUrl: "https://youtu.be/Ds9gn_VH5_k",
      thumbnail: "/assets/tutorials/test-image.jpg",
      instructor: "Dr. Sarah Johnson",
      rating: 4.9,
      students: 2100
    },
    {
      id: 3,
      title: "How to Use Short Term Model",
      description: "Learn how to effectively use the Short Term Model for your trading strategy and quick decision making.",
      content: "This tutorial covers how to use the Short Term Model feature, including setting up your trading parameters, analyzing short-term trends, and making quick trading decisions...",
      level: "intermediate",
      category: "technical",
      duration: "1:07 min",
      videoUrl: "/tutorials/short-term-model.mp4",
      youtubeUrl: "https://youtu.be/YifAPK6n1Bs",
      thumbnail: "/assets/tutorials/short-term-model.jpg",
      instructor: "Michael Chen",
      rating: 4.7,
      students: 1800
    },
    {
      id: 4,
      title: "How to Use Swing Model",
      description: "Learn how to effectively use the Swing Model for your swing trading strategy and medium-term positions.",
      content: "This tutorial covers how to use the Swing Model feature, including setting up your swing trading parameters, analyzing swing patterns, and making medium-term trading decisions...",
      level: "intermediate",
      category: "risk",
      duration: "1:09 min",
      videoUrl: "/tutorials/swing-model.mp4",
      youtubeUrl: "https://youtu.be/ruCvzd6DqCA",
      thumbnail: "/assets/tutorials/swing-model.jpg",
      instructor: "Lisa Rodriguez",
      rating: 4.8,
      students: 1650
    },
    {
      id: 5,
      title: "How to Use AI Stock Analyst",
      description: "Learn how to effectively use the AI Stock Analyst for advanced market analysis and intelligent trading insights.",
      content: "This tutorial covers how to use the AI Stock Analyst feature, including setting up AI analysis parameters, interpreting AI insights, and making data-driven trading decisions...",
      level: "advanced",
      category: "strategies",
      duration: "1:19 min",
      videoUrl: "/tutorials/ai-stock-analyst.mp4",
      youtubeUrl: "https://youtu.be/VLzMaZiUrzE",
      thumbnail: "/assets/tutorials/ai-stock-analyst.jpg",
      instructor: "David Park",
      rating: 4.9,
      students: 950
    },
  ], []);

  const levels = [
    { id: 'all', name: 'All Levels', count: tutorials.length },
    { id: 'beginner', name: 'Beginner', count: tutorials.filter(t => t.level === 'beginner').length },
    { id: 'intermediate', name: 'Intermediate', count: tutorials.filter(t => t.level === 'intermediate').length },
    { id: 'advanced', name: 'Advanced', count: tutorials.filter(t => t.level === 'advanced').length }
  ];

  const categories = [
    { id: 'all', name: 'All Categories', count: tutorials.length },
    { id: 'platform', name: 'Platform', count: tutorials.filter(t => t.category === 'platform').length },
    { id: 'fundamentals', name: 'Fundamentals', count: tutorials.filter(t => t.category === 'fundamentals').length },
    { id: 'technical', name: 'Technical Analysis', count: tutorials.filter(t => t.category === 'technical').length },
    { id: 'risk', name: 'Risk Management', count: tutorials.filter(t => t.category === 'risk').length },
    { id: 'strategies', name: 'Trading Strategies', count: tutorials.filter(t => t.category === 'strategies').length },
    { id: 'technology', name: 'Technology', count: tutorials.filter(t => t.category === 'technology').length },
    { id: 'portfolio', name: 'Portfolio Management', count: tutorials.filter(t => t.category === 'portfolio').length },
    { id: 'psychology', name: 'Market Psychology', count: tutorials.filter(t => t.category === 'psychology').length }
  ];

  useEffect(() => {
    const fetchTutorials = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/tutorials/`);
        if (response.ok) {
          const data = await response.json();
          setTutorials(data);
        } else {
          // Fallback to local data if API fails
          setTutorials(sampleTutorials);
        }
      } catch (error) {
        console.error('Error fetching tutorials:', error);
        // Fallback to local data if API fails
        setTutorials(sampleTutorials);
      } finally {
        setLoading(false);
      }
    };

    fetchTutorials();
  }, [sampleTutorials]);

  const filteredTutorials = tutorials.filter(tutorial => {
    const levelMatch = selectedLevel === 'all' || tutorial.level === selectedLevel;
    const categoryMatch = selectedCategory === 'all' || tutorial.category === selectedCategory;
    return levelMatch && categoryMatch;
  });

  // Handle tutorial click
  const handleTutorialClick = (tutorial) => {
    setSelectedTutorial(tutorial);
    setShowVideoModal(true);
  };

  // Close video modal
  const closeVideoModal = () => {
    setShowVideoModal(false);
    setSelectedTutorial(null);
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'platform': return '🖥️';
      case 'fundamentals': return '📚';
      case 'technical': return '📊';
      case 'risk': return '🛡️';
      case 'strategies': return '🎯';
      case 'technology': return '🤖';
      case 'portfolio': return '💼';
      case 'psychology': return '🧠';
      default: return '📖';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-purple-600 to-blue-600 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Trading & Investment Tutorials
              </h1>
              <p className="text-xl md:text-2xl opacity-90 max-w-3xl mx-auto">
                Master the art of trading and investing with our comprehensive video tutorials designed for all skill levels.
              </p>
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="py-8 bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-6">
              {/* Level Filter */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Filter by Level:</h3>
                <div className="flex flex-wrap gap-3">
                  {levels.map((level) => (
                    <button
                      key={level.id}
                      onClick={() => setSelectedLevel(level.id)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                        selectedLevel === level.id
                          ? 'bg-blue-600 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {level.name} ({level.count})
                    </button>
                  ))}
                </div>
              </div>

              {/* Category Filter */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Filter by Category:</h3>
                <div className="flex flex-wrap gap-3">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                        selectedCategory === category.id
                          ? 'bg-purple-600 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {getCategoryIcon(category.id)} {category.name} ({category.count})
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tutorials Grid */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                <span className="ml-3 text-gray-600">Loading tutorials...</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredTutorials.map((tutorial) => (
                  <div key={tutorial.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
                    {/* Video Thumbnail */}
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={tutorial.thumbnail} 
                        alt={tutorial.title}
                        className="w-full h-full object-cover"
                        onLoad={() => {/* console.log('Image loaded:', tutorial.thumbnail) */}}
                        onError={(e) => {
                          // console.log('Image failed to load:', tutorial.thumbnail);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-br from-purple-400 to-blue-500 flex items-center justify-center" style={{display: 'none'}}>
                        <div className="text-white text-center">
                          <div className="text-4xl mb-2">🎥</div>
                          <div className="text-sm opacity-90">{tutorial.duration}</div>
                        </div>
                      </div>
                      <div className="absolute top-4 left-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLevelColor(tutorial.level)}`}>
                          {tutorial.level.charAt(0).toUpperCase() + tutorial.level.slice(1)}
                        </span>
                      </div>
                      <div className="absolute top-4 right-4">
                        <span className="bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs">
                          {tutorial.duration}
                        </span>
                      </div>
                    </div>
                    
                    <div className="p-6">
                      {/* Category */}
                      <div className="flex items-center text-sm text-gray-500 mb-3">
                        <span className="mr-2">{getCategoryIcon(tutorial.category)}</span>
                        <span className="capitalize">{tutorial.category}</span>
                      </div>
                      
                      {/* Title */}
                      <h2 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                        {tutorial.title}
                      </h2>
                      
                      {/* Description */}
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {tutorial.description}
                      </p>
                      
                      {/* Instructor and Stats */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-600">
                              {tutorial.instructor.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <span className="ml-2 text-sm text-gray-600">{tutorial.instructor}</span>
                        </div>
                        
                        <div className="flex items-center text-sm text-gray-500">
                          <span className="text-yellow-500 mr-1">⭐</span>
                          <span>{tutorial.rating}</span>
                          <span className="mx-1">•</span>
                          <span>{tutorial.students} students</span>
                        </div>
                      </div>
                      
                      {/* Action Button */}
                      <button
                        onClick={() => handleTutorialClick(tutorial)}
                        className="block w-full bg-purple-600 hover:bg-purple-700 text-white text-center py-3 rounded-lg font-medium transition-colors"
                      >
                        Start Tutorial
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {filteredTutorials.length === 0 && !loading && (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">No Tutorials Found</h3>
                <p className="text-gray-600">No tutorials available for the selected filters. Try adjusting your search criteria.</p>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gray-900 py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Start Learning?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Join thousands of traders who are already improving their skills with our comprehensive tutorials.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-lg font-medium text-lg transition-colors"
              >
                Start Free Trial
              </Link>
              <Link
                to="/pricing"
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-4 rounded-lg font-medium text-lg transition-colors"
              >
                View Pricing
              </Link>
            </div>
          </div>
        </section>
      </main>
      
      {/* Video Modal */}
      {showVideoModal && selectedTutorial && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-xl font-bold text-gray-900">{selectedTutorial.title}</h3>
              <button
                onClick={closeVideoModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6">
              {/* Video Player */}
              {selectedTutorial.youtubeUrl ? (
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    className="absolute top-0 left-0 w-full h-full rounded-lg"
                    src={`https://www.youtube.com/embed/${selectedTutorial.youtubeUrl.split('v=')[1]?.split('&')[0] || selectedTutorial.youtubeUrl.split('/').pop()}`}
                    title={selectedTutorial.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              ) : (
                <div className="bg-gray-100 rounded-lg p-8 text-center">
                  <div className="text-gray-400 mb-4">
                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p className="text-gray-600">Video not available</p>
                </div>
              )}
              
              {/* Tutorial Info */}
              <div className="mt-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">About this tutorial</h4>
                <p className="text-gray-600 mb-4">{selectedTutorial.description}</p>
                
                <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {selectedTutorial.duration}
                  </span>
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {selectedTutorial.instructor}
                  </span>
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                    {selectedTutorial.rating} ({selectedTutorial.students} students)
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default TutorialsPage;
